import { getApps, initializeApp } from "firebase/app";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  linkWithCredential,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  updatePassword,
} from "firebase/auth";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";
export const TENANT_CODE =
  (import.meta.env.VITE_TENANT_CODE as string | undefined) ?? "demo-toronto";
const tokenKey = `nanostylist:global:${TENANT_CODE}`;
const legacySessionKey = `${tokenKey}:legacy-password`;

type ApiEnvelope<T> = { data?: T; error?: { message?: string } };
const currentLanguage = () =>
  localStorage.getItem("nanostylist:lang") === "zh" ? "zh" : "en";
const serviceMessage = (status?: number) =>
  currentLanguage() === "zh"
    ? `服务刚刚重新连接${status ? `（${status}）` : ""}，请再试一次。`
    : `The service just reconnected${status ? ` (${status})` : ""}. Please try again.`;

async function readApiEnvelope<T>(
  response: Response,
): Promise<ApiEnvelope<T> | null> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text.trim()) throw new Error(serviceMessage(response.status));
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    throw new Error(
      response.ok
        ? serviceMessage(response.status)
        : `Request failed (${response.status})`,
    );
  }
}

async function fetchApiEnvelope<T>(
  url: string,
  init: RequestInit,
  retryRead = false,
): Promise<{ response: Response; payload: ApiEnvelope<T> | null }> {
  const attempts = retryRead ? 2 : 1;
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const response = await fetch(url, init);
    try {
      const payload = await readApiEnvelope<T>(response);
      if (
        retryRead &&
        attempt + 1 < attempts &&
        [502, 503, 504].includes(response.status)
      ) {
        await new Promise((resolve) => setTimeout(resolve, 180));
        continue;
      }
      return { response, payload };
    } catch (error) {
      lastError = error;
      if (!retryRead || attempt + 1 >= attempts) throw error;
      await new Promise((resolve) => setTimeout(resolve, 180));
    }
  }
  throw lastError instanceof Error ? lastError : new Error(serviceMessage());
}

export function firebaseConfigured() {
  return Boolean(
    import.meta.env.VITE_FIREBASE_API_KEY &&
      import.meta.env.VITE_FIREBASE_PROJECT_ID,
  );
}

export function firebaseGoogleConfigured() {
  return firebaseConfigured() &&
    import.meta.env.VITE_FIREBASE_GOOGLE_ENABLED === "true";
}

function firebaseAuth() {
  if (!firebaseConfigured()) return null;
  const app =
    getApps()[0] ??
    initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    });
  return getAuth(app);
}

export function getSessionToken() {
  return localStorage.getItem(tokenKey);
}
export function clearSession() {
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(legacySessionKey);
  void firebaseAuth()?.signOut();
}

async function exchangeFirebaseToken(idToken: string) {
  const { response, payload } = await fetchApiEnvelope<{ token: string }>(
    `${API_BASE}/api/auth/firebase`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-tenant-code": TENANT_CODE,
      },
      body: JSON.stringify({ idToken }),
    },
  );
  if (!response.ok)
    throw new Error(
      payload?.error?.message ?? "Unable to start a NanoStylist session",
    );
  if (!payload?.data) throw new Error(serviceMessage(response.status));
  localStorage.removeItem(legacySessionKey);
  localStorage.setItem(tokenKey, payload.data.token);
  return payload.data;
}

export async function signIn() {
  if (!firebaseGoogleConfigured()) {
    throw new Error("Google sign-in is not enabled for this deployment.");
  }
  const auth = firebaseAuth()!;
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  return exchangeFirebaseToken(await result.user.getIdToken(true));
}

export async function signInWithEmail(email: string, password: string) {
  const auth = firebaseAuth();
  if (!auth) return testLogin(email, password, "customer");
  let result;
  try {
    result = await signInWithEmailAndPassword(auth, email.trim(), password);
  } catch (error) {
    const code = typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
    if (!["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"].includes(code)) throw error;
    return legacyPasswordLogin(email, password);
  }
  if (!result.user.emailVerified) {
    await sendEmailVerification(result.user);
    await auth.signOut();
    throw new Error(
      "Please verify your email first. We sent you a new verification link.",
    );
  }
  return exchangeFirebaseToken(await result.user.getIdToken(true));
}

async function legacyPasswordLogin(email: string, password: string) {
  const { response, payload } = await fetchApiEnvelope<{
    token: string;
    roles: string[];
    user: { id: string; nickname: string; email: string };
  }>(`${API_BASE}/api/auth/password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-tenant-code": TENANT_CODE },
    body: JSON.stringify({ email: email.trim(), password }),
  });
  if (!response.ok) throw new Error(payload?.error?.message ?? "Email or password is incorrect");
  if (!payload?.data) throw new Error(serviceMessage(response.status));
  localStorage.setItem(tokenKey, payload.data.token);
  localStorage.setItem(legacySessionKey, JSON.stringify({ email: payload.data.user.email }));
  return payload.data;
}

export async function testLogin(
  email: string,
  password: string,
  surface: "customer" | "merchant" = "customer",
) {
  const { response, payload } = await fetchApiEnvelope<{
    token: string;
    roles: string[];
    user: { id: string; nickname: string; email: string };
  }>(`${API_BASE}/api/auth/test-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-tenant-code": TENANT_CODE },
    body: JSON.stringify({ email: email.trim(), password, surface }),
  });
  if (!response.ok) throw new Error(payload?.error?.message ?? "Test sign-in failed");
  if (!payload?.data) throw new Error(serviceMessage(response.status));
  localStorage.setItem(tokenKey, payload.data.token);
  return payload.data;
}

export async function registerWithEmail(email: string, password: string, displayName?:string) {
  const auth = firebaseAuth();
  if (!auth)
    throw new Error("Email registration requires Firebase configuration.");
  const result = await createUserWithEmailAndPassword(
    auth,
    email.trim(),
    password,
  );
  if(displayName?.trim())await updateProfile(result.user,{displayName:displayName.trim()});
  await sendEmailVerification(result.user);
  await auth.signOut();
}

export async function requestPasswordReset(
  email: string,
  language: "en" | "zh" = "en",
) {
  const auth = firebaseAuth();
  if (!auth) throw new Error("Password reset requires Firebase configuration.");
  auth.languageCode = language === "zh" ? "zh-CN" : "en";
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch {
    // The UI intentionally returns the same result for every address to avoid account enumeration.
  }
}

export type AccountSecurity = {
  email: string;
  hasPassword: boolean;
  providers: string[];
  firebaseEnabled: boolean;
  legacyPassword?: boolean;
};
export async function getAccountSecurity(): Promise<AccountSecurity> {
  const auth = firebaseAuth();
  if (!auth)
    return {
      email: "Local development account",
      hasPassword: false,
      providers: ["development"],
      firebaseEnabled: false,
    };
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) {
    const stored = localStorage.getItem(legacySessionKey);
    if (stored) {
      const legacy = JSON.parse(stored) as { email?: string };
      return { email: legacy.email ?? "", hasPassword: true, providers: ["legacy-password"], firebaseEnabled: true, legacyPassword: true };
    }
  }
  return {
    email: user?.email ?? "",
    hasPassword: Boolean(
      user?.providerData.some((value) => value.providerId === "password"),
    ),
    providers: user?.providerData.map((value) => value.providerId) ?? [],
    firebaseEnabled: true,
  };
}

export async function changeAccountPassword(
  currentPassword: string,
  newPassword: string,
) {
  if (localStorage.getItem(legacySessionKey)) {
    const { response, payload } = await fetchApiEnvelope<never>(`${API_BASE}/api/auth/password/change`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-tenant-code": TENANT_CODE,
        Authorization: `Bearer ${getSessionToken() ?? ""}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!response.ok) throw new Error(payload?.error?.message ?? "Unable to update password");
    return;
  }
  const auth = firebaseAuth();
  if (!auth)
    throw new Error("Password management requires Firebase configuration.");
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user || !user.email)
    throw new Error("Please sign in again before changing your password.");
  const hasPassword = user.providerData.some(
    (value) => value.providerId === "password",
  );
  if (hasPassword) {
    if (!currentPassword) throw new Error("Enter your current password.");
    await reauthenticateWithCredential(
      user,
      EmailAuthProvider.credential(user.email, currentPassword),
    );
    await updatePassword(user, newPassword);
  } else {
    await linkWithCredential(
      user,
      EmailAuthProvider.credential(user.email, newPassword),
    );
  }
  return exchangeFirebaseToken(await user.getIdToken(true));
}

export async function devLogin(role: "customer" | "merchant" = "customer") {
  const { response, payload } = await fetchApiEnvelope<{ token: string }>(
    `${API_BASE}/api/auth/dev`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-tenant-code": TENANT_CODE,
      },
      body: JSON.stringify({ role }),
    },
  );
  if (!response.ok)
    throw new Error(payload?.error?.message ?? "Demo sign-in is unavailable");
  if (!payload?.data) throw new Error(serviceMessage(response.status));
  localStorage.setItem(tokenKey, payload.data.token);
  return payload.data;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  authenticated = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("x-tenant-code", TENANT_CODE);
  if (init.body && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  const token = getSessionToken();
  if (authenticated) {
    if (!token) throw new Error("SIGN_IN_REQUIRED");
    headers.set("authorization", `Bearer ${token}`);
  }
  const method = (init.method ?? "GET").toUpperCase();
  const { response, payload } = await fetchApiEnvelope<T>(
    `${API_BASE}${path}`,
    { ...init, headers },
    method === "GET" || method === "HEAD",
  );
  if (!response.ok)
    throw new Error(
      payload?.error?.message ?? `Request failed (${response.status})`,
    );
  return payload?.data as T;
}
